import React, { useCallback, useEffect, useMemo, useState } from 'react'
import BaseModal from './BaseModal'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { setTrackNameItemToEdit, setTrackNameModal, setTrackNameToDuplicate } from '../../store/reducers/modal.reducer'
import { Alert, AppBar, Autocomplete, Box, Button, CircularProgress, Container, Dialog, Divider, FormControl, FormControlLabel, FormLabel, Grid, IconButton, InputLabel, LinearProgress, MenuItem, OutlinedInput, Select, TextField, Toolbar, Typography } from '@mui/material'
import { t } from 'i18next'
import { LoadingButton } from '@mui/lab'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { TShoeing } from '../../shared/types/shoeing.type'
import { get, isEqual, toNumber } from 'lodash'
import { TUser } from '../../shared/types/user.type'

import { hands, trackGroup, trackSurfaces } from '../../enums/trackGroup.enums'
import { Ttrack, trackSurface } from '../../shared/types/tracks.type'
import { useTracks } from '../../hooks/useTracks'
import PlaceAutoComplete from '../tracks/PlaceAutoComplete'
import { useQueryClient } from 'react-query'
import { byIso } from 'country-code-lookup'
import { TPublicTrack } from '../../shared/types/publicTrack.type'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import { useToken } from '../../hooks/useToken'


function TrackName() {
    const open = useSelector<RootState>(state => state.modals.trackNameModal) as boolean
    const itemToEdit = useSelector<RootState>(state => state.modals.trackNameItemToEdit, isEqual) as Ttrack | null
    const itemToDuplicate = useSelector<RootState>(state => state.modals.trackNameToDuplicate) as Ttrack | null
    const auth = useSelector<RootState>(state => state.user.user) as TUser | null
    const { publicTracksLoading, saveTrack, isSaving, updateTrack, isUpdating, getTrackSurfaces, isSurfacesLoading, getPublicTracks, publicTracks, saveFL, isSavingFL, } = useTracks(false)
    const dispatch = useDispatch()
    const [surfaces, setSurfaces] = useState<Array<trackSurface> | []>([])
    const [selectedPublicTrack, setPublicTrack] = useState<TPublicTrack | null>(null)
    const [adressErr, setAdressErr] = useState(false)
    const { isConnectedAsCsm } = useToken()



    useEffect(() => {
        (async () => {
            if (open) {
                await getPublicTracks()
                let response = await getTrackSurfaces()
                setSurfaces(response)
            }
        })()
    }, [open])

    const query = useQueryClient()


    useEffect(() => {
        if (itemToEdit) {
            setValue('id', itemToEdit.id)
            setValue('name', itemToEdit?.name)
            setValue('track_surface', get(itemToEdit, 'track_surface.@id', ''))
            setValue('comment', itemToEdit?.comment)
            setValue('hand_turn', itemToEdit?.hand_turn)
            setValue('address', itemToEdit?.address)
        }
    }, [itemToEdit, surfaces])

    useEffect(() => {
        if (itemToDuplicate) {
            setValue('name', t('copy').concat(' - ').concat(itemToDuplicate?.name))
            setValue('track_surface', get(itemToDuplicate, 'track_surface.@id', ''))
            setValue('comment', itemToDuplicate?.comment)
            setValue('hand_turn', itemToDuplicate?.hand_turn)
            setValue('address', itemToDuplicate?.address)
        }
    }, [itemToDuplicate, surfaces])

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        getValues,
        control,
        formState: { errors, touchedFields },
    } = useForm<Partial<Ttrack>>({
        defaultValues: {
            track_surface: '',
            name: '',
            address: '',
        }
    })



    const onSubmit: SubmitHandler<Partial<Ttrack>> = (data) => {
        const tData: any =
        {
            trainer: get(auth, '@id')
        }

        // Replace empty string values with null
        Object.entries(data).forEach((el) => {

            tData[el[0]] = el[1] ?? null
        })

        if (!itemToEdit && !data.address) {
            setAdressErr(true)
            return
        }

        let promise = itemToEdit ? updateTrack(tData) : saveTrack(tData)


        promise
            .then(async (track) => {
                //On Success reset Form
                if (selectedPublicTrack) {
                    await updateFinishline(track, selectedPublicTrack?.finish_line_latitude_a, selectedPublicTrack?.finish_line_latitude_b, selectedPublicTrack?.finish_line_longitude_a, selectedPublicTrack?.finish_line_longitude_b)
                }

                if (itemToDuplicate) {
                    await updateFinishline(track, itemToDuplicate.finish_line?.latitude_a, itemToDuplicate.finish_line?.latitude_b, itemToDuplicate.finish_line?.longitude_a, itemToDuplicate.finish_line?.longitude_b)
                }

                handleClose()


                itemToEdit && query.refetchQueries(['trackName', itemToEdit.id])



            }).catch(err => { })
    }



    const updateFinishline = async (track?: Ttrack, latitude_a?: number, latitude_b?: number, longitude_a?: number, longitude_b?: number) => {
        if (!track) {
            console.warn('No track to update for finishline');
            return
        }
        try {
            let flData = await saveFL({
                trainer: auth?.['@id'],
                latitude_a,
                latitude_b,
                longitude_a,
                longitude_b,
            })

            await updateTrack({ id: track.id, finish_line: flData?.['@id'] ?? '' })


        } catch (error) {
            toast(t('error:generic'), { type: 'error' })
        }
    }

    const handleClose = () => {
        dispatch(setTrackNameModal(false))
        setAdressErr(false)
        reset()
        setPublicTrack(null)
        dispatch(setTrackNameToDuplicate(null))
        dispatch(setTrackNameItemToEdit(null))
    }

    const onLocationChange = (value: string | null) => {

        setValue('address', value ?? "")
        setAdressErr(false)
    }


    const groupedTracksByCountry = useMemo(() => {
        return publicTracks.map(track => ({
            ...track,
            name: track.training_center_name.concat(' - ').concat(track.name),
            country_code: byIso(track.country_code)?.country ?? track.country_code
        })).sort((a, b) => {
            const countryCompare = a.country_code.localeCompare(b.country_code);
            if (countryCompare !== 0) return countryCompare;

            // then sort by name within the same country
            return a.name.localeCompare(b.name);
        })
    }, [publicTracks])

    const updateValueFromPublicTrack = (publicTrack: TPublicTrack | null) => {
        setPublicTrack(publicTrack)

        if (!publicTrack) return

        let rawPublicTrack = publicTracks.find(track => track.id === publicTrack.id)
        
        if (!rawPublicTrack) return

        setValue('name', rawPublicTrack?.display_name ?? publicTrack?.name)
        setValue('address', rawPublicTrack.address)
        setValue('hand_turn', rawPublicTrack.hand_turn)
        setValue('track_surface', publicTrack?.track_surface?.['@id'])

    }
    if (!open) return null

    return (
        <BaseModal title={itemToEdit ? t('tracks:information') : t('tracks:addTrack')} open={open} handleClose={handleClose}>

            {
                adressErr && <Alert sx={{ my: 1 }} severity='error'>{t('error:selectAdress')}</Alert>
            }
            <form onSubmit={handleSubmit(onSubmit)}>

                <Box>
                    <Autocomplete
                        options={groupedTracksByCountry}
                        groupBy={(option) => option.country_code}
                        isOptionEqualToValue={(option, value) => option['@id'] === value['@id']}
                        loading={publicTracksLoading}
                        onChange={(_, value) => updateValueFromPublicTrack(value)}
                        getOptionKey={option => option['@id']}
                        getOptionLabel={option => option.name}
                        renderInput={(params) => <TextField {...params} label={t('selectFromLib')} />}

                    />
                    <Divider sx={{ my: 2 }}></Divider>

                    <Grid container spacing={3}>
                        <Grid item md={12} xs={12}>

                            <FormControl fullWidth error={errors?.hasOwnProperty('name')}>
                                <InputLabel
                                required
                                error={errors.hasOwnProperty('name')} >{t('editTrainingForm:trackName:label')}</InputLabel>

                                <Controller
                                    control={control}
                                    name='name'
                                    render={({ field }) => (
                                        <OutlinedInput
                                            required
                                            {...field}
                                            label={t('editTrainingForm:trackName:label')}></OutlinedInput>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <PlaceAutoComplete defaultValue={getValues('address')} onValueChange={onLocationChange}></PlaceAutoComplete>
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <FormControl fullWidth error={!!errors.track_surface}>
                                <InputLabel
                                    required // Adds the asterisk (*)
                                    error={!!errors.track_surface}
                                >
                                    {t('editTrainingForm:trackSurface:label')}
                                </InputLabel>
                                <Controller
                                    name="track_surface"
                                    control={control}
                                    // 1. Add validation rules
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            error={!!errors.track_surface}
                                            disabled={isSurfacesLoading}
                                            required
                                            label={t('editTrainingForm:trackSurface:label')}
                                        >
                                            {surfaces.map(surface => (
                                                <MenuItem key={surface['@id']} value={surface['@id']}>
                                                    {t(surface?.translation_key?.replaceAll('.', ':') ?? "")}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {/* 2. Optional: Add a helper text for the error */}
                                {errors.track_surface && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {t('notifications:field_required')}
                                    </Typography>
                                )}
                                {isSurfacesLoading && <LinearProgress sx={{ height: 2 }}></LinearProgress>}
                            </FormControl>
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <FormControl fullWidth >
                                <InputLabel error={errors.hasOwnProperty('hand_turn')} >{t('eap:table:headers:hand')}</InputLabel>
                                <Controller
                                    name="hand_turn"
                                    control={control}
                                    rules={{}}
                                    render={({ field }) => (
                                        <Select  {...field} label={t('eap:table:headers:hand')}>
                                            {hands.map(hand => (
                                                <MenuItem key={hand.key} value={hand.value}>{t(hand.key)}</MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />

                            </FormControl>
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <TextField multiline rows={3} error={errors?.hasOwnProperty('comment')} {...register('comment', { required: false })} fullWidth label={t('qualification:comment')}></TextField>

                        </Grid>
                    </Grid>


                </Box>
                <Box display={'flex'} mt={2} justifyContent={'end'}>
                    <LoadingButton loading={isSaving || isUpdating || isSavingFL} type='submit' variant='contained'>{itemToEdit ? t('button:edit') : t('button:save')}</LoadingButton>
                </Box>
            </form>

        </BaseModal >
    )
}

export default TrackName